import React from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Text,
  VStack,
  Badge,
  Link as ChakraLink,
  IconButton,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import AppLayout from "../components/layout/AppLayout";
import PageHeader from "../components/PageHeader";

const brand = {
  green: "#1C4C3F",
  cream: "#F7F5EB",
  mint: "#D9EFE6",
  ink: "#0E2721",
};

const ResourceCard = ({ tag, title, body, href }) => (
  <Box
    bg="#FFFFFF"
    border="1px solid #EAEAEA"
    borderRadius="lg"
    p={{ base: 5, md: 6 }}
    boxShadow="sm"
    _hover={{ boxShadow: "md", transform: "translateY(-2px)" }}
    transition="all .15s ease"
    h="100%"
    display="flex"
    flexDirection="column"
  >
    {/* Top content */}
    <VStack align="start" spacing={3} flex="1">
      <Badge bg={brand.mint} color={brand.ink} borderRadius="md" px={2} py={1}>
        {tag}
      </Badge>
      <Heading size="md" color={brand.ink}>{title}</Heading>
      <Text color="gray.700">{body}</Text>
    </VStack>

    {/* Footer pinned to bottom */}
    <Box mt="auto">
      <HStack pt={1}>
        <Button
          as={ChakraLink}
          href={href}
          isExternal
          rightIcon={<ExternalLinkIcon />}
          bg={brand.green}
          color="white"
          _hover={{ opacity: 0.92 }}
          size="sm"
        >
          Open in Notion
        </Button>
        <IconButton
          aria-label="Open in Notion"
          as={ChakraLink}
          href={href}
          isExternal
          icon={<ExternalLinkIcon />}
          variant="ghost"
        />
      </HStack>
      <Text fontSize="xs" color="gray.500" mt={2}>
        Tip: In Notion, click <strong>Duplicate</strong> to copy the template to your workspace.
      </Text>
    </Box>
  </Box>
);

const FreeResources = () => {
  return (
    <AppLayout>
      <Box bg={brand.cream} color={brand.ink}>
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 10, md: 14 }}>
          <PageHeader
            title="Free Resources"
            subtitle="Operator-friendly templates you can duplicate and use today."
          />

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={6}
            alignItems="stretch"             // ⬅️ ensure all GridItems stretch
          >
            <GridItem>
              <ResourceCard
                tag="Category / Buyer"
                title="CPG Category Review Template"
                body="Structure your buyer-facing category story: market size, trends, velocity/ACV snapshot, white space, and the ask."
                href="https://sly-sombrero-ee5.notion.site/CPG_Category_Review_Template-257392fe9faa800ca8b6ca41f826a3ea"
              />
            </GridItem>

            <GridItem>
              <ResourceCard
                tag="Pricing / Trade"
                title="Pricing & Trade Promo Tracker"
                body="Simple tracker for promos across retailers: type, dates, base/promo price, estimated vs. actual lift, and notes."
                href="https://sly-sombrero-ee5.notion.site/Pricing_Trade_Promo_Tracker-257392fe9faa80f29a67c536ea5bd89f"
              />
            </GridItem>

            <GridItem>
              <ResourceCard
                tag="Launch"
                title="New Item Launch Checklist"
                body="Kanban checklist covering UPCs, distributor setup, sell sheets, deck updates, pricing guardrails, and launch comms."
                href="https://sly-sombrero-ee5.notion.site/New_Item_Launch_Checklist-257392fe9faa80479217e7fd48a7ebc6"
              />
            </GridItem>
          </Grid>

          {/* Optional: CTA to suggest more templates */}
          <Box mt={10} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg" p={5}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <Text>Want a template we’re missing? Tell us and we’ll add it.</Text>
              <Button
                as={ChakraLink}
                href="mailto:hello@shelfkey.co?subject=Template%20request"
                bg={brand.green}
                color="white"
                _hover={{ opacity: 0.92 }}
                size="sm"
              >
                Request a template
              </Button>
            </HStack>
          </Box>
        </Container>
      </Box>
    </AppLayout>
  );
};

export default FreeResources;
